/**
 * Connect — "who is alerted" for one client (OMON-375, ADR-0030 Amendment 3).
 *
 * The business decides what a client's care package buys when their sensors
 * raise an alert: carers alerted 24/7, the office team in working hours, family
 * only, or recorded with nobody alerted. The notification hub reads this on
 * every carer alert, and its hourly reachability watch only pages when a
 * client who SHOULD be alerting has no phone that can receive it.
 *
 * Reads and writes go through the `responder_disposition` callable, never a
 * direct Firestore write: the EpisodeOfCare `extension` array also holds the
 * IoT anchor's DocumentReference, and the server changes only the one entry,
 * audited in the same batch.
 *
 * Admin + planner only (the callable enforces it too). A change that STOPS
 * carers being alerted needs an explicit second click inside the panel (no
 * browser confirm(): those wedge automation).
 */
import { escapeHtml } from "../utils/html";

export type Disposition = "" | "staffed-response" | "office-hours-triage" | "family-only" | "record-only";

export interface DispositionState {
  value: Disposition;
  carersAlerted: boolean;
  reachablePhones: number | null;
  /** Am.6: opted-in, signed-in family phones (the hub's reachability watch). */
  familyPhones?: number | null;
  checkedAt: string | null;
}

/** The options, in the order shown. Wording is the business's, not FHIR's. */
export const OPTIONS: { value: Disposition; label: string; detail: string }[] = [
  { value: "staffed-response", label: "Carers are alerted, 24/7",
    detail: "Sensor alerts go to the carers on shift. If nobody confirms, they're reminded, "
      + "then the office is told in working hours. Family members who receive alerts "
      + "get them too, and respond when no carer is on shift." },
  { value: "office-hours-triage", label: "Office team, working hours",
    detail: "No 24/7 escalation. Carers still get the alert as a plain push for now." },
  // ADR-0030 Am.6 (2026-10-22): this used to say "Family see it in their app",
  // which nothing delivered — choosing it alerted NOBODY. Now it does what it says.
  { value: "family-only", label: "Family only",
    detail: "No carer is alerted. Family members who receive alerts (below) get every "
      + "alert in the Circle of Support app and are the ones who respond." },
  { value: "record-only", label: "Recorded, nobody alerted",
    detail: "Kept in the client's record. Nobody's phone is alerted." },
  { value: "", label: "Not decided yet",
    detail: "Kept in the client's record, and nobody is alerted until someone decides." },
];

// "" is unset, which means record (OMON-405, Ed 2026-11-03).
const NO_CARER_ALERTS: Disposition[] = ["family-only", "record-only", ""];

/** Pure. Mirrors the hub: unset alerts nobody; an unknown value still alerts carers. */
export function carersAlerted(v: Disposition): boolean {
  return !NO_CARER_ALERTS.includes(v);
}

/** Pure. True when the change stops carers being alerted (needs a second click). */
export function silences(before: Disposition, after: Disposition): boolean {
  return carersAlerted(before) && !carersAlerted(after);
}

export function labelFor(v: Disposition): string {
  return OPTIONS.find((o) => o.value === v)?.label ?? "Not decided yet";
}

/**
 * Pure. The one-line reachability note, or "" when there's nothing to say.
 * The case to act on: alerting is expected, and no phone can receive it.
 */
export function reachabilityNote(s: DispositionState): { text: string; warn: boolean } {
  if (!s.carersAlerted) return { text: "", warn: false };
  if (s.reachablePhones === null) return { text: "Phone reach not checked yet.", warn: false };
  if (s.reachablePhones === 0) {
    // ADR-0030 Am.6 (Ed 2026-10-26): opted-in family phones receive every alert
    // too, so they are told — but the office still expects a carer to respond.
    const f = s.familyPhones ?? 0;
    if (f > 0) {
      return { text: `No carer phone yet — only family (${f} phone${f === 1 ? "" : "s"}) `
        + "receive this client's alerts.", warn: true };
    }
    return { text: "No phone can currently receive this client's alerts.", warn: true };
  }
  const n = s.reachablePhones;
  return { text: `${n} phone${n === 1 ? "" : "s"} can receive this client's alerts.`, warn: false };
}

/** Pure. The toolbar button's text and whether it shows a warning. Am.7:
 * `unwatched` (known only once the dialog has been opened) warns too. */
export function buttonFor(s: DispositionState, unwatched: string[] = []): { text: string; warn: boolean } {
  const warn = reachabilityNote(s).warn || unwatched.length > 0;
  return { text: `Alerts: ${labelFor(s.value)}${warn ? " ⚠" : ""}`, warn };
}

// ── ADR-0030 Am.7: per-kind choices (backlog/docs/ADR-0030-am7-alert-choices-contract.md) ──

export type Push = "always" | "window" | "off";
export interface KindChoice { push: Push; from?: string; to?: string }
export type KindsMap = Record<string, KindChoice>;

/** The mutable kinds, in the contract's order. Emergencies are never a choice. */
export const KINDS: { key: string; label: string }[] = [
  { key: "motion", label: "Movement (motion sensors)" },
  { key: "door", label: "Doors and windows" },
  { key: "power", label: "Plugs (appliance use)" },
  { key: "environment", label: "Temperature and room sensors" },
  { key: "device-health", label: "Device online and offline" },
  { key: "other", label: "Any other sensor" },
];
const SENSOR_KINDS = ["motion", "door", "power", "environment"];
/** The window the dialog suggests (Ed, 2026-10-25: motion 22:00–07:00). */
export const SUGGESTED_WINDOW = { from: "22:00", to: "07:00" };

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function allAlways(): KindsMap {
  return Object.fromEntries(KINDS.map((k) => [k.key, { push: "always" as Push }]));
}

/** Pure. The v1 off list as kinds (contract: client-monitoring ⇒ the four
 * sensor kinds; device-health ⇒ device-health). */
export function kindsFromOff(off: string[]): KindsMap {
  const out = allAlways();
  if (off.includes("client-monitoring")) SENSOR_KINDS.forEach((k) => { out[k] = { push: "off" }; });
  if (off.includes("device-health")) out["device-health"] = { push: "off" };
  return out;
}

/** Pure. A tier's effective kinds from the callable; an older callable that
 * returns no `kinds` falls back to its v1 off list, so existing data shows. */
export function kindsFrom(v: unknown, v1Off: string[]): KindsMap {
  if (!v || typeof v !== "object" || Array.isArray(v)) return kindsFromOff(v1Off);
  const raw = v as Record<string, unknown>;
  const out = allAlways();
  for (const { key } of KINDS) {
    const c = raw[key] as Record<string, unknown> | undefined;
    if (!c || typeof c !== "object") continue;
    if (c.push === "off") out[key] = { push: "off" };
    else if (c.push === "window" && typeof c.from === "string" && typeof c.to === "string") {
      out[key] = { push: "window", from: c.from, to: c.to };
    }
  }
  return out;
}

/** Pure. Mirrors lib/alert_kinds.py project_off_categories: the v1 list a
 * hub that reads only v1 gets — never more off than the preset. */
export function projectOff(k: KindsMap): string[] {
  const off = (key: string) => k[key]?.push === "off";
  const out: string[] = [];
  if ([...SENSOR_KINDS, "other"].every(off)) out.push("client-monitoring");
  if (off("device-health")) out.push("device-health");
  return out;
}

const RANK: Record<Push, number> = { always: 0, window: 1, off: 2 };
const choiceOf = (k: KindsMap, key: string): KindChoice => k[key] ?? { push: "always" };
const sameChoice = (a: KindChoice, b: KindChoice) =>
  a.push === b.push && (a.push !== "window" || (a.from === b.from && a.to === b.to));

export function sameKinds(a: KindsMap, b: KindsMap): boolean {
  return KINDS.every(({ key }) => sameChoice(choiceOf(a, key), choiceOf(b, key)));
}

/** Pure. True when any kind narrows (needs the second click), as the server decides. */
export function kindsSilence(before: KindsMap, after: KindsMap): boolean {
  return KINDS.some(({ key }) => {
    const b = choiceOf(before, key);
    const a = choiceOf(after, key);
    return RANK[a.push] > RANK[b.push] || (a.push === "window" && b.push === "window" && !sameChoice(a, b));
  });
}

/** Pure. "" when every window is valid, else the reason (shown, Save disabled). */
export function kindsProblem(k: KindsMap): string {
  for (const { key, label } of KINDS) {
    const c = choiceOf(k, key);
    if (c.push !== "window") continue;
    if (!HHMM.test(c.from ?? "") || !HHMM.test(c.to ?? "")) return `${label}: choose both times.`;
    if (c.from === c.to) return `${label}: the window must start and end at different times.`;
  }
  return "";
}

/** Pure. What a tier narrows, for a read-only line: "" when it narrows nothing. */
export function kindsSummary(k: KindsMap): string {
  return KINDS.flatMap(({ key, label }) => {
    const c = choiceOf(k, key);
    if (c.push === "off") return [`${label}: off`];
    if (c.push === "window") return [`${label}: only ${c.from}–${c.to}`];
    return [];
  }).join("; ");
}

/** ADR-0030 Am.8: the two emergency kinds a FAMILY member may silence
 * (quiet hours, snooze, off — behind confirmation in the CoS app). */
export const EMERGENCY_KINDS: { key: string; label: string }[] = [
  { key: "pressure-pad", label: "Out of bed / pressure pad" },
  { key: "emergency-button", label: "Emergency button" },
];

/** Am.8: one family member's standing emergency silence (initials only). */
export interface EmergencySilence { member: string; kind: string; summary: string; confirmedAt: string | null }

/** Am.7 "Unwatched", from `family_alerts_optin`'s `watch` (null = not known).
 * Am.8 adds `emergencySilences` and `emergenciesUnwatched` (empty from an
 * older callable). */
export interface FamilyWatch {
  familyOnly: boolean; optedIn: number; unwatched: string[];
  emergencySilences: EmergencySilence[]; emergenciesUnwatched: string[];
}

export function watchFrom(v: unknown): FamilyWatch | null {
  if (!v || typeof v !== "object") return null;
  const w = v as Record<string, unknown>;
  const known = KINDS.map((k) => k.key);
  const emKnown = EMERGENCY_KINDS.map((k) => k.key);
  const str = (x: unknown) => (typeof x === "string" ? x : "");
  return {
    familyOnly: w.familyOnly === true,
    optedIn: typeof w.optedIn === "number" ? w.optedIn : 0,
    unwatched: Array.isArray(w.unwatched) ? known.filter((k) => (w.unwatched as unknown[]).includes(k)) : [],
    emergencySilences: Array.isArray(w.emergencySilences)
      ? (w.emergencySilences as unknown[]).flatMap((s) => {
        if (!s || typeof s !== "object") return [];
        const r = s as Record<string, unknown>;
        if (!emKnown.includes(str(r.kind)) || !str(r.summary)) return [];
        return [{ member: str(r.member) || "?", kind: str(r.kind), summary: str(r.summary),
          confirmedAt: str(r.confirmedAt) || null }];
      })
      : [],
    emergenciesUnwatched: Array.isArray(w.emergenciesUnwatched)
      ? emKnown.filter((k) => (w.emergenciesUnwatched as unknown[]).includes(k)) : [],
  };
}

/** Pure. The kinds nobody is alerted about, as labels — only for family-only clients. */
export function unwatchedLabels(w: FamilyWatch | null): string[] {
  if (!w || !w.familyOnly) return [];
  return w.unwatched.map((k) => KINDS.find((o) => o.key === k)?.label ?? k);
}

export function emergencyLabel(kind: string): string {
  return EMERGENCY_KINDS.find((o) => o.key === kind)?.label ?? kind;
}

/** Pure. Am.8: the emergency kinds every family responder has silenced NOW,
 * as labels — only for family-only clients. */
export function emergenciesUnwatchedLabels(w: FamilyWatch | null): string[] {
  if (!w || !w.familyOnly) return [];
  return w.emergenciesUnwatched.map(emergencyLabel);
}

/** Pure. Am.8: "A.L. — Out of bed / pressure pad: quiet 06:00–09:00 daily",
 * one line per standing silence (who, what, until when). */
export function emergencySilenceLines(w: FamilyWatch | null): string[] {
  return (w?.emergencySilences ?? []).map(
    (s) => `${s.member} — ${emergencyLabel(s.kind)}: ${s.summary}`);
}

/** ADR-0030 Phase 4: the v1 categories (`offCategories`). Since Am.7 the
 * dialog sets per-kind choices (KINDS); these are kept to READ v1 data. */
export const OFFABLE: { key: string; label: string }[] = [
  { key: "client-monitoring", label: "Sensor alerts (door, movement, presence)" },
  { key: "device-health", label: "Device online and offline" },
];

/** Pure. The second-click warning. ☠️ Only a disposition change stops
 * emergencies; a category turned off never does, so saying so would be false. */
export function confirmText(dispositionSilences: boolean, orgWide = ""): string {
  if (dispositionSilences) {
    return "This stops carers being alerted for this client's sensors, including emergencies. "
      + "The change is recorded with your name.";
  }
  const who = orgWide ? `every client in ${orgWide}` : "this client";
  return `This stops some carer alerts for ${who}. Emergency button and pressure-pad `
    + "alerts still come through. The change is recorded with your name.";
}

/** Pure. True when the policy change turns MORE off (needs the second click). */
export function policySilences(before: string[], after: string[]): boolean {
  return after.some((c) => !before.includes(c));
}

/** Both policy tiers for one client (ADR-0030 Am.2 §2): the client's own and
 * its organisation's. `canEditOrg` = the caller is an admin and the client
 * has a managing organisation. */
export interface PolicyState {
  off: string[];
  orgOff: string[];
  orgName: string;
  canEditOrg: boolean;
  /** ADR-0030 Am.7: each tier's EFFECTIVE carer preset per kind. */
  kinds: KindsMap;
  orgKinds: KindsMap;
  /** False when the callable here predates Am.7 (it saves only v1 on/off). */
  v2: boolean;
  /** ADR-0030 Am.5 step 4. null = not set at that tier (the next applies). */
  timers: Timers;
  orgTimers: Timers;
  orgOfficeHours: OfficeHours | null;
}

export interface Timers { remindMinutes: number | null; escalateMinutes: number | null }
export interface OfficeHours { days: number[]; start: string; end: string }

/** Mirrors lib/notification_policy.py and the hub's escalation.py (Ed, 2026-10-19). */
export const DEFAULT_TIMERS = { remindMinutes: 10, escalateMinutes: 20 };
export const DEFAULT_OFFICE_HOURS: OfficeHours = { days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" };
export const MAX_MINUTES = 240;
export const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const NO_TIMERS: Timers = { remindMinutes: null, escalateMinutes: null };
const EMPTY_POLICY: PolicyState = {
  off: [], orgOff: [], orgName: "", canEditOrg: false,
  kinds: allAlways(), orgKinds: allAlways(), v2: false,
  timers: NO_TIMERS, orgTimers: NO_TIMERS, orgOfficeHours: null,
};

/** Pure. Only the known offable keys, in display order. */
export function offList(v: unknown): string[] {
  return Array.isArray(v) ? OFFABLE.map((o) => o.key).filter((k) => v.includes(k)) : [];
}

function timersFrom(v: unknown): Timers {
  const t = (v ?? {}) as Record<string, unknown>;
  const n = (x: unknown) => (typeof x === "number" ? x : null);
  return { remindMinutes: n(t.remindMinutes), escalateMinutes: n(t.escalateMinutes) };
}

/** Pure. A timer box's value: a number, null (empty = inherit), or "invalid". */
export function parseMinutes(s: string): number | null | "invalid" {
  const t = s.trim();
  if (t === "") return null;
  if (!/^\d+$/.test(t)) return "invalid";
  const n = Number(t);
  return n >= 1 && n <= MAX_MINUTES ? n : "invalid";
}

/** Pure. What applies when a tier sets nothing: the organisation's, else the default. */
export function inherited(org: Timers): { remindMinutes: number; escalateMinutes: number } {
  return {
    remindMinutes: org.remindMinutes ?? DEFAULT_TIMERS.remindMinutes,
    escalateMinutes: org.escalateMinutes ?? DEFAULT_TIMERS.escalateMinutes,
  };
}

/** Pure. "" when the boxes are acceptable, else the reason (shown, Save disabled). */
export function timerProblem(remind: string, escalate: string, fallback: Timers): string {
  const r = parseMinutes(remind);
  const e = parseMinutes(escalate);
  if (r === "invalid" || e === "invalid") return `Use a whole number of minutes, 1 to ${MAX_MINUTES}.`;
  const rr = r ?? fallback.remindMinutes;
  const ee = e ?? fallback.escalateMinutes;
  if (rr !== null && ee !== null && rr >= ee) return "The reminder must come before the office is told.";
  return "";
}

/** Pure. "Mon–Fri 09:00–17:00" style. */
export function hoursSummary(h: OfficeHours): string {
  const days = [...h.days].sort().map((d) => DAYS[d - 1]).join(", ");
  return `${days}, ${h.start}–${h.end}`;
}

/** ADR-0030 Am.6: one of the client's family contacts, as the office sees it. */
export interface FamilyContact {
  rpDocId: string;
  display: string;
  hasCosAccess: boolean;
  receivesAlerts: boolean;
}

/** Pure. The contacts list from a callable response, tolerant of shape. */
export function familyList(v: unknown): FamilyContact[] {
  if (!Array.isArray(v)) return [];
  return v.filter((c) => c && typeof c.rpDocId === "string").map((c) => ({
    rpDocId: c.rpDocId,
    display: typeof c.display === "string" ? c.display : "(no name)",
    hasCosAccess: c.hasCosAccess === true,
    receivesAlerts: c.receivesAlerts === true,
  }));
}

/** Pure. The switches that differ from what is stored — one save each.
 * A contact without Circle of Support access can't be switched ON (the
 * callable refuses too: there is no phone to alert). */
export function familyChanges(
  contacts: FamilyContact[], on: string[],
): { rpDocId: string; receivesAlerts: boolean }[] {
  return contacts
    .filter((c) => on.includes(c.rpDocId) !== c.receivesAlerts)
    .filter((c) => c.hasCosAccess || !on.includes(c.rpDocId))
    .map((c) => ({ rpDocId: c.rpDocId, receivesAlerts: on.includes(c.rpDocId) }));
}

interface FamilyResult { contacts: FamilyContact[]; watch: FamilyWatch | null }

async function callFamily(data: Record<string, unknown>): Promise<FamilyResult> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const { app } = await import("../firebase");
  const call = httpsCallable(getFunctions(app, "europe-west2"), "family_alerts_optin");
  const d = (await call(data)).data as Record<string, unknown>;
  // Am.7: `watch` is absent from an older callable — then nothing is shown.
  return { contacts: familyList(d.contacts), watch: watchFrom(d.watch) };
}

async function callPolicy(data: Record<string, unknown>): Promise<PolicyState> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const { app } = await import("../firebase");
  const call = httpsCallable(getFunctions(app, "europe-west2"), "notification_policy");
  const d = (await call(data)).data as Record<string, unknown>;
  const hours = d.orgOfficeHours as Record<string, unknown> | null | undefined;
  const off = offList(d.offCategories);
  const orgOff = offList(d.orgOffCategories);
  return {
    off,
    orgOff,
    orgName: typeof d.orgName === "string" ? d.orgName : "",
    canEditOrg: d.canEditOrg === true,
    kinds: kindsFrom(d.kinds, off),
    orgKinds: kindsFrom(d.orgKinds, orgOff),
    v2: Array.isArray(d.alertKinds),
    timers: timersFrom(d.timers),
    orgTimers: timersFrom(d.orgTimers),
    orgOfficeHours: hours && Array.isArray(hours.days)
      ? { days: hours.days as number[], start: String(hours.start), end: String(hours.end) }
      : null,
  };
}

async function callDisposition(data: Record<string, unknown>): Promise<DispositionState> {
  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const { app } = await import("../firebase");
  const call = httpsCallable(getFunctions(app, "europe-west2"), "responder_disposition");
  const res = await call(data);
  const d = res.data as Record<string, unknown>;
  return {
    value: (d.value as Disposition) ?? "",
    carersAlerted: d.carersAlerted !== false,
    reachablePhones: typeof d.reachablePhones === "number" ? d.reachablePhones : null,
    familyPhones: typeof d.familyPhones === "number" ? d.familyPhones : null,
    checkedAt: typeof d.checkedAt === "string" ? d.checkedAt : null,
  };
}

/** Wire the toolbar button: load the current state, then open the panel on click. */
export function mountDispositionButton(
  btn: HTMLButtonElement, eocDocId: string, clientName: string,
): void {
  let state: DispositionState | null = null;
  let unwatched: string[] = [];   // Am.7: known once the dialog has opened
  let emUnwatched: string[] = []; // Am.8: likewise
  const paint = () => {
    if (!state) return;
    const b = buttonFor(state, [...unwatched, ...emUnwatched]);
    btn.textContent = b.text;
    btn.style.color = b.warn ? "#b45309" : "";
    btn.title = [reachabilityNote(state).text,
      unwatched.length ? `Unwatched: ${unwatched.join(", ")}.` : "",
      emUnwatched.length ? `Emergencies unwatched: ${emUnwatched.join(", ")}.` : ""].filter(Boolean).join(" ")
      || "Who is alerted when this client's sensors raise an alert";
  };
  // ☠️ NO network call on page load (2026-10-18). Web builds reach the
  // non-prod environments on every merge, but the functions only with
  // release/omon, so a page-load call to a callable that isn't deployed there
  // yet logged CORS errors on every Connect page and failed the test journeys.
  // Shown from the claims (admin/planner, the callable's own gate); the state
  // is fetched only when someone opens the panel.
  btn.hidden = true;   // fail-closed until the claim is positively confirmed
  // 2026-10-29 (Ed): the FIRST click waited ~5 s with nothing moving — three
  // callables waking from cold while the button merely greyed. Three fixes:
  //  1. the reads start on the first GESTURE towards the button (hover or
  //     keyboard focus), a few hundred ms before the click — never on mount:
  //     the 2026-10-18 rule stands and its test below pins it (a page-load
  //     call to an undeployed callable broke every Connect page);
  //  2. the click opens the panel's chrome at once, as a skeleton, while the
  //     reads land — Cancel works throughout;
  //  3. the three callables keep one warm instance (functions/main.py).
  // `load` is defined AFTER the click listener on purpose (the guard test reads
  // the source up to it); the handlers run long after this function returns.
  type Loaded = [PromiseSettledResult<DispositionState>, PromiseSettledResult<PolicyState>,
    PromiseSettledResult<FamilyResult>];
  let loading: Promise<Loaded> | null = null;
  let settled = false;
  const forget = () => { loading = null; settled = false; };
  void (async () => {
    // Lazy, like the callables: keeps this module importable by unit tests.
    const { auth } = await import("../firebase");
    const { loadClaims } = await import("../admin/claims");
    const user = auth.currentUser;
    if (!user) return;
    const claims = await loadClaims(user);
    if (claims?.role === "admin" || claims?.role === "planner") btn.hidden = false;
  })();
  btn.addEventListener("pointerenter", () => { void load(); });   // (1)
  btn.addEventListener("focus", () => { void load(); });
  btn.addEventListener("click", async () => {
    btn.disabled = true;
    const idleText = btn.textContent;
    btn.textContent = "Opening…";
    const skeleton = settled ? null : openSkeleton(clientName);   // (2)
    const [d, p, f] = await load();
    skeleton?.remove();
    if (skeleton?.cancelled) {
      btn.disabled = false;
      btn.textContent = idleText;
      return;
    }
    if (d.status === "rejected") {
      forget();
      btn.disabled = false;
      btn.textContent = "Alerts: not available yet";
      btn.title = "This setting isn't available in this environment yet.";
      return;
    }
    // A fresh read next time: the panel may save, and warm functions make a
    // re-read cheap — only the session's first open paid the cold start.
    forget();
    state = d.value;
    // A failed policy read shows nothing off; the disposition still works.
    const policy = p.status === "fulfilled" ? p.value : EMPTY_POLICY;
    // null = not available in this environment yet; the section says so.
    const family: FamilyContact[] | null = f.status === "fulfilled" ? f.value.contacts : null;
    const watch: FamilyWatch | null = f.status === "fulfilled" ? f.value.watch : null;
    // Am.8: emergencies nobody is watching now warn the toolbar too.
    unwatched = unwatchedLabels(watch);
    emUnwatched = emergenciesUnwatchedLabels(watch);
    btn.disabled = false;
    paint();
    openPanel(eocDocId, clientName, state, policy, family, watch, (s) => { state = s; paint(); });
  });
  // The three reads are independent, so they go out together: the panel waits
  // for the slowest one, not the sum (~8 s one after another, 2026-10-26).
  // Am.7: the family call also carries "Unwatched". Shared by the gesture
  // prefetch and the click; one in-flight read at a time.
  function load(): Promise<Loaded> {
    loading ??= Promise.allSettled([
      callDisposition({ episodeId: eocDocId, action: "get" }),
      callPolicy({ episodeId: eocDocId, action: "get" }),
      callFamily({ episodeId: eocDocId, action: "get" }),
    ]).then((r) => { settled = true; return r as Loaded; });
    return loading;
  }
}

/** The panel's chrome, shown the instant the Alerts button is clicked while
 * its three reads are still landing (2026-10-29). Same frame as the real
 * panel so it does not jump; grey bars where the sections will be; Cancel
 * and × work, and are reported through `cancelled` so the click handler
 * does not open the real panel afterwards. */
export function openSkeleton(clientName: string): { remove: () => void; cancelled: boolean } {
  document.getElementById("dispositionSkeleton")?.remove();
  const overlay = document.createElement("div");
  overlay.id = "dispositionSkeleton";
  Object.assign(overlay.style, {
    position: "fixed", inset: "0", background: "rgba(17,24,39,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: "10000",
  });
  const panel = document.createElement("div");
  Object.assign(panel.style, {
    background: "#ffffff", borderRadius: "10px", width: "min(520px, 94vw)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)", font: "15px system-ui, sans-serif",
    maxHeight: "92vh", display: "flex", flexDirection: "column",
  });
  const bar = (w: string) => `<div style="height:14px;width:${w};border-radius:7px;background:#e5e7eb;margin:8px 12px"></div>`;
  panel.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;padding:12px;border-bottom:1px solid #e5e7eb">
      <strong style="flex:1">Who is alerted — ${escapeHtml(clientName)}</strong>
      <button id="dispSkelClose" type="button" aria-label="Close"
              style="border:none;background:none;font-size:17px;cursor:pointer;color:#6b7280">×</button>
    </div>
    <div style="padding:8px 0" aria-busy="true">
      ${bar("62%")}${bar("48%")}${bar("70%")}${bar("40%")}${bar("55%")}
      <div style="padding:6px 12px 8px;color:#6b7280;font-size:13px">Loading this client's alert settings…</div>
    </div>
    <div style="display:flex;gap:8px;justify-content:flex-end;padding:12px;border-top:1px solid #e5e7eb">
      <button id="dispSkelCancel" type="button"
              style="padding:6px 12px;border:1px solid #d1d5db;border-radius:6px;background:#ffffff;cursor:pointer">Cancel</button>
      <button type="button" disabled
              style="padding:6px 12px;border:none;border-radius:6px;background:#2563eb;color:#ffffff;opacity:0.5">Save</button>
    </div>`;
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  const handle = { remove: () => overlay.remove(), cancelled: false };
  const cancel = () => { handle.cancelled = true; overlay.remove(); };
  panel.querySelector("#dispSkelClose")?.addEventListener("click", cancel);
  panel.querySelector("#dispSkelCancel")?.addEventListener("click", cancel);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) cancel(); });
  return handle;
}

/** A folded section: closed by default, its one-line summary says what it
 * holds so nobody has to open it to know. The caller closes `</details>`. */
export function fold(title: string, summary: string): string {
  // Pure (no DOM), so it is unit-testable like the rest of this module's helpers.
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return `
    <details style="padding:8px 12px;border-top:1px solid #e5e7eb;font-size:13px">
      <summary style="cursor:pointer;margin-bottom:6px"><strong>${esc(title)}</strong>
        <span style="color:#6b7280"> — ${esc(summary)}</span></summary>`;
}

function openPanel(
  eocDocId: string, clientName: string, current: DispositionState, policy: PolicyState,
  family: FamilyContact[] | null, watch: FamilyWatch | null,
  onSaved: (s: DispositionState) => void,
): void {
  // ADR-0030 Am.7 "Unwatched": family-only clients only, and only when known.
  const unwatched = unwatchedLabels(watch);
  const unwatchedNote = !unwatched.length ? "" : `
      <div style="margin:4px 0;padding:6px 8px;border-radius:6px;background:#fef2f2;
                  border:1px solid #fecaca;color:#991b1b">
        <strong>Unwatched: ${escapeHtml(unwatched.join(", "))}.</strong>
        ${watch && watch.optedIn === 0
          ? "No family member receives alerts for this client, so nobody is alerted."
          : "Every family member who receives alerts has turned these off, so nobody is alerted about them."}
      </div>`;
  // ADR-0030 Am.6 (Q8): the office opts each family member in, here.
  // 2026-10-26: every section below the disposition folds (Ed: the panel was
  // taller than the screen). A closed fold still says what it holds, and the
  // Unwatched warning stays outside its fold so it is never hidden.
  // ADR-0030 Am.8: emergencies every family responder has silenced right
  // now — outside any fold, so it is never hidden.
  const emUnwatched = emergenciesUnwatchedLabels(watch);
  const emergencyNote = !emUnwatched.length ? "" : `
      <div style="margin:4px 0;padding:6px 8px;border-radius:6px;background:#fef2f2;
                  border:1px solid #fecaca;color:#991b1b">
        <strong>Emergencies unwatched: ${escapeHtml(emUnwatched.join(", "))}.</strong>
        In office hours the office is told instead; out of hours nobody is.
      </div>`;
  const silenceLines = emergencySilenceLines(watch);
  const silenceList = !silenceLines.length ? "" : `
      <div style="margin:6px 0 2px"><strong>Emergencies family have silenced</strong></div>
      <ul style="margin:0 0 6px;padding-left:20px">
        ${silenceLines.map((l) => `<li>${escapeHtml(l)}</li>`).join("")}
      </ul>`;
  const receiving = family?.filter((c) => c.receivesAlerts).length ?? 0;
  const notes = `${unwatchedNote}${emergencyNote}`;
  const familySection = `${notes ? `<div style="padding:0 12px">${notes}</div>` : ""}
    ${fold("Family (Circle of Support)",
      (family === null ? "not available yet"
        : receiving === 0 ? "nobody receives alerts"
          : `${receiving} receive${receiving === 1 ? "s" : ""} alerts`)
      + (silenceLines.length
        ? `; ${silenceLines.length} emergency silence${silenceLines.length === 1 ? "" : "s"}` : ""))}
      <div style="color:#6b7280;margin-bottom:4px">Family members who receive alerts get
        emergencies and device offline alerts to start with, choose the other kinds in their
        app, and are the ones who respond when no carer is on shift. The carer choices above
        don't apply to them.</div>
      ${silenceList}
      ${family === null
        ? `<div style="color:#6b7280">Not available in this environment yet.</div>`
        : family.length === 0
          ? `<div style="color:#6b7280">No family contacts on file for this client.</div>`
          : family.map((c) => `
        <label style="display:flex;gap:8px;padding:4px 0;align-items:flex-start;
                      cursor:${c.hasCosAccess ? "pointer" : "default"}">
          <input type="checkbox" name="familyAlerts" value="${escapeHtml(c.rpDocId)}"
                 ${c.receivesAlerts ? "checked" : ""}
                 ${c.hasCosAccess || c.receivesAlerts ? "" : "disabled"} style="margin-top:2px">
          <span>${escapeHtml(c.display)} — receives alerts
            ${c.hasCosAccess ? "" : `<br><span style="color:#6b7280">No Circle of Support
              access yet: give them access first.</span>`}</span>
        </label>`).join("")}
    </details>`;
  const orgLabel = policy.orgName || "this client's organisation";
  // Am.7: one row per alert kind — Always / Only between <from>–<to> / Off.
  const kindRows = (tier: "client" | "org", k: KindsMap) => KINDS.map((o) => {
    const c = k[o.key] ?? { push: "always" };
    const id = `${tier}-${o.key}`;
    const sel = (p: Push) => (c.push === p ? "selected" : "");
    return `
        <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:3px 0;font-size:13px">
          <span style="flex:1;min-width:11em">${escapeHtml(o.label)}</span>
          <select data-kind-tier="${tier}" data-kind="${escapeHtml(o.key)}"
                  style="padding:2px 4px;border:1px solid #d1d5db;border-radius:4px">
            <option value="always" ${sel("always")}>Always</option>
            <option value="window" ${sel("window")}>Only between…</option>
            <option value="off" ${sel("off")}>Off</option>
          </select>
          <span data-window="${escapeHtml(id)}" ${c.push === "window" ? "" : "hidden"}>
            <input type="time" data-from="${escapeHtml(id)}"
                   value="${escapeHtml(c.from ?? SUGGESTED_WINDOW.from)}">–<input type="time"
                   data-to="${escapeHtml(id)}" value="${escapeHtml(c.to ?? SUGGESTED_WINDOW.to)}">
          </span>
        </div>`;
  }).join("");
  const orgNarrows = kindsSummary(policy.orgKinds);
  // Am.5 step 4: what an empty box means at each tier, shown as its placeholder.
  const orgFallback = inherited(policy.orgTimers);
  const hours = policy.orgOfficeHours ?? DEFAULT_OFFICE_HOURS;
  const box = (id: string, value: number | null, placeholder: number) => `
        <input id="${id}" type="text" inputmode="numeric" size="4"
               value="${value === null ? "" : value}" placeholder="${placeholder}"
               style="width:4em;padding:2px 4px;border:1px solid #d1d5db;border-radius:4px">`;
  const orgSection = policy.canEditOrg
    ? `
    ${fold(`Every client in ${orgLabel} (admins)`,
      `${orgNarrows || "carers get every kind"}; office ${hoursSummary(hours)}`)}
      <div style="color:#6b7280;font-size:13px;margin-bottom:4px">Carers only: family choose
        their own. A client's own setting can narrow this further, never widen it.
        Emergency button and pressure-pad alerts always come through.</div>
      ${kindRows("org", policy.orgKinds)}
      <div style="font-size:13px;padding:6px 0 2px">For every client in ${escapeHtml(orgLabel)}
        unless set below: remind after ${box("orgRemind", policy.orgTimers.remindMinutes, DEFAULT_TIMERS.remindMinutes)}
        min, tell the office after ${box("orgEscalate", policy.orgTimers.escalateMinutes, DEFAULT_TIMERS.escalateMinutes)} min.</div>
      <div style="font-size:13px;padding:2px 0">Office open:
        ${DAYS.map((d, i) => `<label style="cursor:pointer;margin-right:4px"><input type="checkbox"
           name="officeDay" value="${i + 1}" ${hours.days.includes(i + 1) ? "checked" : ""}>${d}</label>`).join("")}
        <input id="officeStart" type="time" value="${escapeHtml(hours.start)}">–<input id="officeEnd"
           type="time" value="${escapeHtml(hours.end)}">
        <span style="color:#6b7280">(bank holidays are always closed)</span></div>
    </details>`
    : `<div style="padding:8px 12px;font-size:13px;color:#6b7280">
         ${orgNarrows ? `For carers of every client in ${escapeHtml(orgLabel)}:
           ${escapeHtml(orgNarrows)}.<br>` : ""}
         Office hours for ${escapeHtml(orgLabel)}: ${escapeHtml(hoursSummary(hours))}.</div>`;
  document.getElementById("dispositionOverlay")?.remove();
  const overlay = document.createElement("div");
  overlay.id = "dispositionOverlay";
  Object.assign(overlay.style, {
    position: "fixed", inset: "0", background: "rgba(17,24,39,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: "10000",
  });
  const panel = document.createElement("div");
  Object.assign(panel.style, {
    background: "#ffffff", borderRadius: "10px", width: "min(520px, 94vw)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)", font: "15px system-ui, sans-serif",
    // Fits the screen: the title and the Save row stay put, #dispBody scrolls.
    maxHeight: "92vh", display: "flex", flexDirection: "column",
  });
  const note = reachabilityNote(current);
  const options = OPTIONS.map((o) => `
      <label style="display:flex;gap:8px;padding:8px 12px;cursor:pointer;align-items:flex-start">
        <input type="radio" name="disposition" value="${escapeHtml(o.value)}"
               ${o.value === current.value ? "checked" : ""} style="margin-top:4px">
        <span><strong>${escapeHtml(o.label)}</strong><br>
          <span style="color:#6b7280;font-size:13px">${escapeHtml(o.detail)}</span></span>
      </label>`).join("");
  panel.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;padding:12px;border-bottom:1px solid #e5e7eb">
      <strong style="flex:1">Who is alerted — ${escapeHtml(clientName)}</strong>
      <button id="dispClose" type="button" aria-label="Close"
              style="border:none;background:none;font-size:17px;cursor:pointer;color:#6b7280">×</button>
    </div>
    <div id="dispBody" style="flex:1;min-height:0;overflow-y:auto">
    <div style="padding:8px 0">${options}</div>
    ${fold("This client's carers are alerted about",
      kindsSummary(policy.kinds) || "every kind, any time")}
      <div style="color:#6b7280;font-size:13px;margin-bottom:4px">Carers only: family choose
        their own in the Circle of Support app. Emergency button and pressure-pad alerts
        always come through. Times are UK time; a window can run past midnight.</div>
      ${kindRows("client", policy.kinds)}
    </details>
    ${fold("If no carer on shift confirms",
      `remind after ${policy.timers.remindMinutes ?? orgFallback.remindMinutes} min, `
      + `office after ${policy.timers.escalateMinutes ?? orgFallback.escalateMinutes} min`)}
      <div style="color:#6b7280;margin-bottom:4px">For "Carers are alerted, 24/7". Out of office hours nobody else is told yet.</div>
      Remind them after ${box("remindMinutes", policy.timers.remindMinutes, orgFallback.remindMinutes)} min,
      tell the area coordinator and the office after ${box("escalateMinutes", policy.timers.escalateMinutes, orgFallback.escalateMinutes)} min.
      <div style="color:#6b7280">Leave a box empty to use ${escapeHtml(orgLabel)}'s setting.</div>
    </details>${familySection}${orgSection}
    <div id="dispReach" style="padding:8px 12px;font-size:13px;color:${note.warn ? "#b45309" : "#6b7280"}">
      ${escapeHtml(note.text)}</div>
    </div>
    <div id="dispConfirm" hidden style="margin:8px 12px 0;padding:8px 12px;border-radius:6px;
         background:#fef2f2;border:1px solid #fecaca;color:#991b1b;font-size:13px">
      This stops carers being alerted for this client's sensors, including emergencies.
      The change is recorded with your name.</div>
    <div style="display:flex;gap:8px;justify-content:flex-end;padding:12px;border-top:1px solid #e5e7eb">
      <span id="dispStatus" style="flex:1;color:#9ca3af;align-self:center;font-size:13px"></span>
      <button id="dispCancel" type="button"
              style="padding:6px 12px;border:1px solid #d1d5db;border-radius:6px;background:#ffffff;cursor:pointer">Cancel</button>
      <button id="dispSave" type="button" disabled
              style="padding:6px 12px;border:none;border-radius:6px;background:#2563eb;color:#ffffff;cursor:pointer;opacity:0.5">Save</button>
    </div>`;
  overlay.appendChild(panel);
  document.body.appendChild(overlay);

  const closeHooks: (() => void)[] = [];
  const close = () => { closeHooks.forEach((h) => h()); overlay.remove(); };
  const save = panel.querySelector<HTMLButtonElement>("#dispSave")!;
  const cancelBtn = panel.querySelector<HTMLButtonElement>("#dispCancel")!;
  let dirty = false;          // something differs from what was loaded
  let discardArmed = false;   // the next close throws the edits away
  const disarmDiscard = () => {
    discardArmed = false;
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.color = "";
  };
  const confirmBox = panel.querySelector<HTMLElement>("#dispConfirm")!;
  const status = panel.querySelector<HTMLElement>("#dispStatus")!;
  let armed = false;   // the second click on a silencing change
  const chosen = (): Disposition =>
    (panel.querySelector<HTMLInputElement>('input[name="disposition"]:checked')?.value ?? "") as Disposition;
  // ── Am.7: the per-kind rows ──
  const chosenKinds = (tier: "client" | "org"): KindsMap => {
    const out = allAlways();
    for (const { key } of KINDS) {
      const id = `${tier}-${key}`;
      const push = (panel.querySelector<HTMLSelectElement>(
        `select[data-kind-tier="${tier}"][data-kind="${key}"]`)?.value ?? "always") as Push;
      out[key] = push === "window"
        ? { push, from: panel.querySelector<HTMLInputElement>(`input[data-from="${id}"]`)?.value ?? "",
          to: panel.querySelector<HTMLInputElement>(`input[data-to="${id}"]`)?.value ?? "" }
        : { push };
    }
    return out;
  };
  const clientKinds = () => chosenKinds("client");
  const orgKinds = () => (policy.canEditOrg ? chosenKinds("org") : policy.orgKinds);
  // ── Am.6: family switches ──
  const familyOn = () => Array.from(
    panel.querySelectorAll<HTMLInputElement>('input[name="familyAlerts"]:checked')).map((i) => i.value);
  const familyDiff = () => familyChanges(family ?? [], familyOn());
  // ── Am.5 step 4: timers + office hours ──
  const text = (id: string) => panel.querySelector<HTMLInputElement>(`#${id}`)?.value ?? "";
  const timerChanges = (remindId: string, escalateId: string, was: Timers) => {
    const out: Record<string, number | null> = {};
    const r = parseMinutes(text(remindId));
    const e = parseMinutes(text(escalateId));
    if (r !== "invalid" && r !== was.remindMinutes) out.remindMinutes = r;
    if (e !== "invalid" && e !== was.escalateMinutes) out.escalateMinutes = e;
    return out;
  };
  const clientTimerChanges = () => timerChanges("remindMinutes", "escalateMinutes", policy.timers);
  const orgTimerChanges = () =>
    (policy.canEditOrg ? timerChanges("orgRemind", "orgEscalate", policy.orgTimers) : {});
  const chosenHours = (): OfficeHours => ({
    days: [1, 2, 3, 4, 5, 6, 7].filter((d) =>
      panel.querySelector<HTMLInputElement>(`input[name="officeDay"][value="${d}"]`)?.checked),
    start: text("officeStart"), end: text("officeEnd"),
  });
  const hoursChanged = () => {
    if (!policy.canEditOrg) return false;
    const was = policy.orgOfficeHours ?? DEFAULT_OFFICE_HOURS;
    const now = chosenHours();
    return now.start !== was.start || now.end !== was.end
      || now.days.length !== was.days.length || now.days.some((d) => !was.days.includes(d));
  };
  const problem = (): string => {
    // The client's empty boxes fall back to the organisation's values as edited.
    const orgNow = policy.canEditOrg
      ? { remindMinutes: parseMinutes(text("orgRemind")), escalateMinutes: parseMinutes(text("orgEscalate")) }
      : policy.orgTimers;
    const orgProblem = policy.canEditOrg
      ? timerProblem(text("orgRemind"), text("orgEscalate"), DEFAULT_TIMERS) : "";
    if (orgProblem) return `${orgLabel}: ${orgProblem}`;
    const kindsWhy = kindsProblem(clientKinds()) || (policy.canEditOrg ? kindsProblem(orgKinds()) : "");
    if (kindsWhy) return kindsWhy;
    const clean = (v: number | null | "invalid") => (v === "invalid" ? null : v);
    const clientProblem = timerProblem(text("remindMinutes"), text("escalateMinutes"),
      inherited({ remindMinutes: clean(orgNow.remindMinutes), escalateMinutes: clean(orgNow.escalateMinutes) }));
    if (clientProblem) return clientProblem;
    if (policy.canEditOrg) {
      const h = chosenHours();
      if (!h.days.length) return "Choose at least one day the office is open.";
      if (!(h.start && h.end && h.end > h.start)) return "The office must close after it opens.";
    }
    return "";
  };
  const refresh = () => {
    const v = chosen();
    const changed = v !== current.value || !sameKinds(clientKinds(), policy.kinds)
      || !sameKinds(orgKinds(), policy.orgKinds)
      || Object.keys(clientTimerChanges()).length > 0 || Object.keys(orgTimerChanges()).length > 0
      || hoursChanged() || familyDiff().length > 0;
    const why = problem();
    armed = false;
    confirmBox.hidden = true;
    status.textContent = why;
    status.style.color = why ? "#b45309" : "#9ca3af";
    const ok = changed && !why;
    dirty = changed;
    disarmDiscard();
    save.disabled = !ok;
    save.style.opacity = ok ? "1" : "0.5";
    save.style.background = "#2563eb";
    save.textContent = "Save";
  };
  panel.querySelectorAll('input[name="disposition"], input[name="officeDay"], input[name="familyAlerts"]')
    .forEach((r) => r.addEventListener("change", refresh));
  panel.querySelectorAll("#remindMinutes, #escalateMinutes, #orgRemind, #orgEscalate, #officeStart, #officeEnd, input[data-from], input[data-to]")
    .forEach((r) => r.addEventListener("input", refresh));
  panel.querySelectorAll<HTMLSelectElement>("select[data-kind-tier]").forEach((s) =>
    s.addEventListener("change", () => {
      const span = panel.querySelector<HTMLElement>(
        `span[data-window="${s.dataset.kindTier}-${s.dataset.kind}"]`);
      if (span) span.hidden = s.value !== "window";
      refresh();
    }));
  // Apple HIG (Modality): "help people avoid data loss by getting confirmation
  // before closing a modal view". With unsaved changes, every way out (×,
  // Cancel, a click outside, Escape) first turns Cancel into "Discard changes";
  // only a second, deliberate click throws the edits away.
  const tryClose = () => {
    if (!dirty || discardArmed) { close(); return; }
    discardArmed = true;
    cancelBtn.textContent = "Discard changes";
    cancelBtn.style.color = "#991b1b";
    status.textContent = "You have unsaved changes. Save them, or click Discard changes.";
    status.style.color = "#b45309";
    cancelBtn.focus();
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") tryClose(); };
  document.addEventListener("keydown", onKey);
  closeHooks.push(() => document.removeEventListener("keydown", onKey));
  panel.querySelector("#dispClose")?.addEventListener("click", tryClose);
  cancelBtn.addEventListener("click", tryClose);
  overlay.addEventListener("click", (e) => { if (e.target === overlay) tryClose(); });

  save.addEventListener("click", async () => {
    const v = chosen();
    const kinds = clientKinds();
    const dispChanged = v !== current.value;
    const offChanged = !sameKinds(kinds, policy.kinds);
    const orgK = orgKinds();
    const orgChanged = !sameKinds(orgK, policy.orgKinds);
    const dispSilences = dispChanged && silences(current.value, v);
    const offSilences = offChanged && kindsSilence(policy.kinds, kinds);
    const orgSilences = orgChanged && kindsSilence(policy.orgKinds, orgK);
    const silencing = dispSilences || offSilences || orgSilences;
    if (silencing && !armed) {
      armed = true;
      confirmBox.textContent = confirmText(dispSilences, orgSilences ? orgLabel : "");
      confirmBox.hidden = false;
      save.textContent = "Yes, stop carer alerts";
      save.style.background = "#dc2626";
      return;
    }
    save.disabled = true;
    status.style.color = "#9ca3af";
    status.textContent = "Saving…";
    try {
      // One call per tier, carrying only what changed there (the callable
      // merges, so one setting never clobbers another). The organisation goes
      // first: the client's reminder-before-escalation check reads its values.
      const orgTimers = orgTimerChanges();
      const orgHours = hoursChanged();
      // Am.7: the whole per-kind map, plus its v1 projection — a callable
      // that predates Am.7 ignores `kinds` and saves only the projection.
      let v1Only = false;
      if (orgChanged || Object.keys(orgTimers).length || orgHours) {
        const res = await callPolicy({
          episodeId: eocDocId, action: "set", level: "org",
          ...(orgChanged ? { kinds: orgK, offCategories: projectOff(orgK), confirmSilence: orgSilences } : {}),
          ...orgTimers,
          ...(orgHours ? { officeHours: chosenHours() } : {}),
        });
        v1Only ||= orgChanged && !res.v2;
      }
      const clientTimers = clientTimerChanges();
      if (offChanged || Object.keys(clientTimers).length) {
        const res = await callPolicy({
          episodeId: eocDocId, action: "set",
          ...(offChanged ? { kinds, offCategories: projectOff(kinds), confirmSilence: offSilences } : {}),
          ...clientTimers,
        });
        v1Only ||= offChanged && !res.v2;
      }
      // Am.6: one save per family switch changed (the callable audits each).
      for (const change of familyDiff()) {
        await callFamily({ episodeId: eocDocId, action: "set", ...change });
      }
      const s = dispChanged
        ? await callDisposition({
          episodeId: eocDocId, action: "set", value: v, confirmSilence: dispSilences,
        })
        : current;
      onSaved(s);
      if (v1Only) {
        // Stay open: the office should see this didn't save as chosen.
        status.style.color = "#b45309";
        status.textContent = "Saved only as on/off: per-kind choices and times "
          + "aren't available in this environment yet.";
        return;
      }
      status.textContent = "Saved.";
      setTimeout(close, 600);
    } catch (err) {
      console.error("[connect] disposition save failed", err);
      status.textContent = err instanceof Error ? err.message : "Save failed.";
      save.disabled = false;
    }
  });
}
